// SupportScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import Header from "../components/Header";
import HostNav from "../components/hostComponents/HostNav";
import request from "../utils/request";
import { Alert, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  LifeBuoy,
  Mail,
  Phone as PhoneIcon,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

/* --------------- Design tokens (aligned with Concierge) --------------- */
const COLORS = {
  bg: "#F8F8F8",
  surface: "#FFFFFF",
  text: "#222222",
  subtext: "#717171",
  border: "#E5E5E5",
  accent: "#FF385C",
  focus: "#FFB3C1",
  success: "#22C55E",
  warn: "#F59E0B",
  info: "#3B82F6",
  muted: "#F4F4F5",
};

/* --------------- Helpers --------------- */
const cn = (...arr) => arr.filter(Boolean).join(" ");

const SupportScreen = () => {
  const { hostInfo: Info } = useSelector((state) => state.host);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");

  // UX state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  // Derived: estimated SLA (feel free to adjust)
  const responseEta = useMemo(() => "Usually within 2–4 hours", []);

  // Prefill from logged-in host
  useEffect(() => {
    setName(Info ? `${Info?.firstName || ""} ${Info?.lastName || ""}`.trim() : "");
    setEmail(Info?.email || "");
    setPhone(Info?.phoneNumber || "");
  }, [Info]);

  // Auto-clear banners with cleanup
  useEffect(() => {
    if (!error && !success) return;
    const t = setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    return () => clearTimeout(t);
  }, [error, success]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactMessage?.trim()) {
      setError("Message cannot be empty");
      return;
    }
    if (!captchaValue) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await request.post("/contact", {
        name,
        email,
        phone,
        message: contactMessage,
        captchaResponse: captchaValue,
      });
      if (data?.success) {
        setSuccess(data.message || "Thanks - your message has been sent.");
        setContactMessage("");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen w-full" style={{ backgroundColor: COLORS.bg }}>
      {Info ? <HostNav /> : <Header />}

      {/* Alerts (float) */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            className="fixed w-[90%] left-1/2 -translate-x-1/2 top-4 z-50"
          >
            <Alert severity="success">{success}</Alert>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            className="fixed w-[90%] left-1/2 -translate-x-1/2 top-4 z-50"
          >
            <Alert severity="warning">{error}</Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <div className="px-6 md:px-10 pt-6">
        <div
          className="relative rounded-3xl overflow-hidden w-full shadow"
          style={{ background: "linear-gradient(135deg, #FFE4EA 0%, #FFF5F7 50%, #FFFFFF 100%)" }}
        >
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 20%, rgba(255,56,92,0.08), transparent 50%)" }} />
          <div className="flex flex-col md:flex-row items-center justify-between px-6 md:px-10 py-8 md:py-10 relative">
            <div className="flex items-center mb-4 md:mb-0">
              <div
                className="w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center mr-4"
                style={{ backgroundColor: "#FFFFFF", border: `1px solid ${COLORS.border}`, color: COLORS.accent }}
              >
                <LifeBuoy className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <div>
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-2"
                  style={{ backgroundColor: "rgba(255,255,255,0.85)", color: COLORS.text, border: `1px solid ${COLORS.border}` }}>
                  Help & Support
                </div>
                <h1 className="text-2xl md:text-3xl font-semibold" style={{ color: COLORS.text }}>
                  We’re here to help - anytime
                </h1>
                <div className="text-sm md:text-base mt-1" style={{ color: COLORS.subtext }}>
                  {responseEta}. Our team replies 7 days a week.
                </div>
              </div>
            </div>

            <div className="mt-3 md:mt-0">
              <div className="rounded-2xl px-4 py-3 flex items-center shadow"
                   style={{ backgroundColor: "#FFFFFF", border: `1px solid ${COLORS.border}` }}>
                <Clock className="w-4 h-4 mr-2" style={{ color: COLORS.info }} />
                <div className="text-sm" style={{ color: COLORS.text }}>
                  Hours: <span className="font-medium">8am–8pm PT</span> • 7 days
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="px-6 md:px-10 py-6 md:py-8 grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
        {/* Left: Contact form */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 rounded-3xl shadow p-6 md:p-8"
          style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}
        >
          <div className="flex items-center mb-5">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mr-3"
              style={{ backgroundColor: "#FAFAFA", border: `1px solid ${COLORS.border}`, color: COLORS.accent }}
            >
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm" style={{ color: COLORS.subtext }}>Contact us</div>
              <h2 className="text-lg font-semibold" style={{ color: COLORS.text }}>Send a message</h2>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <Field
              label="Name"
              value={name}
              onChange={setName}
              readOnly={!!Info}
              placeholder="Your full name"
            />
            {/* Email */}
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              readOnly={!!Info}
              placeholder="you@company.com"
            />
            {/* Phone */}
            <Field
              label="Phone Number"
              value={phone}
              onChange={setPhone}
              readOnly={!!Info}
              placeholder="+1 555 555 5555"
            />
            {/* Spacer */}
            <div className="hidden md:block" />

            {/* Message */}
            <div className="md:col-span-2">
              <Label>Message</Label>
              <textarea
                rows={6}
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Tell us what you need help with…"
                className="w-full rounded-xl px-3 py-2 outline-none"
                style={{ border: `1px solid ${COLORS.border}`, backgroundColor: "#FFFFFF", color: COLORS.text }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 4px ${COLORS.focus}`)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                required
              />
            </div>

            {/* ReCAPTCHA */}
            <div className="md:col-span-2">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq"
                onChange={(token) => setCaptchaValue(token)}
                onExpired={() => {
                  setCaptchaValue(null);
                  recaptchaRef.current?.reset();
                }}
                className="my-2"
              />
            </div>

            {/* Actions */}
            <div className="md:col-span-2 flex items-center justify-between pt-2">
              <div className="text-xs" style={{ color: COLORS.subtext }}>
                You’ll also receive a copy via email for your records.
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-5 py-2.5 rounded-full font-medium"
                style={{ backgroundColor: COLORS.accent, color: "#FFFFFF", opacity: loading ? 0.8 : 1 }}
              >
                {loading ? (
                  <>
                    <CircularProgress color="inherit" size={18} className="mr-2" /> Sending…
                  </>
                ) : (
                  <>
                    Send message <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Right: Helpful panels */}
        <div className="space-y-6">
          {/* Contact options */}
          <Card>
            <div className="flex items-center mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mr-3"
                   style={{ backgroundColor: "#FAFAFA", border: `1px solid ${COLORS.border}`, color: COLORS.info }}>
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm" style={{ color: COLORS.subtext }}>Other ways to reach us</div>
                <div className="text-base font-semibold" style={{ color: COLORS.text }}>Quick contacts</div>
              </div>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center text-sm">
                <Mail className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
                <span style={{ color: COLORS.text }}>
                  Email: <a href="mailto:support@bellhopt.com" className="underline">support@bellhopt.com</a>
                </span>
              </li>
              <li className="flex items-center text-sm">
                <PhoneIcon className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
                <span style={{ color: COLORS.text }}>Phone: +1 (949) 534-6476</span>
              </li>
              <li className="flex items-center text-sm">
                <Clock className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
                <span style={{ color: COLORS.text }}>Hours: 8am–8pm PT • 7 days</span>
              </li>
            </ul>
          </Card>

          {/* System status */}
          <Card>
            <div className="flex items-center mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mr-3"
                   style={{ backgroundColor: "#FAFAFA", border: `1px solid ${COLORS.border}`, color: COLORS.success }}>
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm" style={{ color: COLORS.subtext }}>Platform status</div>
                <div className="text-base font-semibold" style={{ color: COLORS.text }}>All systems operational</div>
              </div>
            </div>
            <div className="text-sm" style={{ color: COLORS.subtext }}>
              If you suspect an outage, send us a message and we’ll investigate immediately.
            </div>
          </Card>

          {/* Tips */}
          <Card>
            <div className="flex items-center mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mr-3"
                   style={{ backgroundColor: "#FAFAFA", border: `1px solid ${COLORS.border}`, color: COLORS.warn }}>
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm" style={{ color: COLORS.subtext }}>Tips</div>
                <div className="text-base font-semibold" style={{ color: COLORS.text }}>Help us help you</div>
              </div>
            </div>
            <ul className="list-disc ml-5 text-sm space-y-2" style={{ color: COLORS.text }}>
              <li>Include your property name and order ID (if applicable).</li>
              <li>Tell us the expected guest arrival date/time for urgent items.</li>
              <li>Share screenshots when something looks off (pricing, items, etc.).</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Small presentational pieces ---------------- */
function Label({ children }) {
  return (
    <label className="text-sm font-medium block mb-2" style={{ color: COLORS.text }}>
      {children}
    </label>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, readOnly = false }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        className={cn(
          "w-full rounded-xl px-3 py-2 outline-none",
          readOnly && "bg-gray-50 cursor-not-allowed"
        )}
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: "#FFFFFF", color: COLORS.text }}
        onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 4px ${COLORS.focus}`)}
        onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
        required
      />
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      className="rounded-2xl p-5 shadow"
      style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}
    >
      {children}
    </div>
  );
}

export default SupportScreen;
